import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';

import OPTIONS from '../Options';

import { withClinicalData } from '../contexts/clinicalData';

import QALayout from '../layout/QA';

import HandPointing from 'images/ask/icons/hand-pointing.svg';

import MaleFront from 'images/ask/male-front.svg';
import MaleBack from 'images/ask/male-back.svg';

import FemaleFront from 'images/ask/female-front.svg';
import FemaleBack from 'images/ask/female-back.svg';

import Navigator from '../components/Navigator';
import Button from 'shared/Button';
import Input from '../components/formElements/Input';

import bodyLocations from '../data/questionnaire/bodyLocations';

import SegmentAnalytics from '../../../helpers/SegmentAnalytics.js';

function titleize(string) {
  return string.replace(/^(.)/, letter => letter.toUpperCase()).replace(/_/g, ' ');
}

class BodyLocator extends Component {
  constructor(props) {
    super(props);

    const patientSex = this.props.clinicalData.patient && this.props.clinicalData.patient.find(p => p.id === 'sex').choice_id;

    this.state = {
      query: '',
      queryRegion: '',
      locations: bodyLocations,
      selection: OPTIONS.bodyLocator.persistLocationsOnBackButton ? props.clinicalData.bodyLocation : [],
      flipped: false,
      frontImage: patientSex === 'female' ? FemaleFront : MaleFront,
      backImage: patientSex === 'female' ? FemaleBack : MaleBack,
      submitting: false,
    };
  }

  render() {
    const { query, queryRegion, selection, flipped } = this.state;
    const matches = this.matches();
    const isQuerying = this.isQuerying();

    return (
      <QALayout
        className="bodylocator-view"
        completionPercentage={60}
        ref={el => this.qaRef = el}
        sidebar={
          <React.Fragment>
            { !isQuerying &&
              <React.Fragment>
                <img className="header-icon" src={HandPointing} />
                <h2>Where was the photo taken?</h2>
                <p>Click on the body figure to show us where you took your photo, or search below.</p>
              </React.Fragment>
            }

            { queryRegion !== '' &&
              <React.Fragment>
                <div className="input">
                  <button className="input-prepend" onClick={this.onQueryRegionChange.bind(this, '')}>
                    {titleize(queryRegion)}&nbsp;⨯
                  </button>
                  <Input className="input bodylocator__body-region-search-input" type="text" placeholder={`Search ‘${titleize(queryRegion)}‘`} onChange={this.onQueryChange.bind(this)} value={query} autoFocus={true} />
                </div>
              </React.Fragment>
            }

            { queryRegion === '' &&
              <Input className="input bodylocator__body-part-search-input" type="text" placeholder="Search" onChange={this.onQueryChange.bind(this)} value={query} autoFocus={true} />
            }

            { isQuerying &&
              <React.Fragment>
                <p className="label">{ matches.length } { matches.length === 1 ? 'match' : 'matches' }</p>
                <div className="matches bodylocator__search-results">
                  { matches.map(item => {
                    return (
                      <div key={item.id} className="match bodylocator__search-results__item" onClick={this.toggleSelection.bind(this, item)}>
                        <div className="match-contents">
                          <span className="match-contents-label">
                            { item.label }
                          </span>

                          <span className="match-contents-button">
                            { !this.isSelected(item) && <button className="oval bodylocator__search-results__item-toggle"></button>}
                            { this.isSelected(item) && <button className="oval oval-inverse bodylocator__search-results__item-toggle active"></button>}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </React.Fragment>
            }
          </React.Fragment>
        }
        content={
          <div>
            <button className="button button-flip" onClick={this.onFlipClick.bind(this)}>Flip to back</button>
            <div className="model">
              <div className="selection">
                { selection.map(item => {
                  return (
                    <div key={item.id}>
                      <Button className="button button-selection" onClick={this.toggleSelection.bind(this, item)}>
                        {item.label}
                                                                          &nbsp;&nbsp;
                        <span className="oval oval-inverse"></span>
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="body-image">
                <div style={{ display: flipped ? 'none' : 'inline' }}>
                  <SVG src={this.state.frontImage} onClick={this.onBodyClick.bind(this)} onLoad={this.syncSelection.bind(this)} />
                </div>
                <div style={{ display: flipped ? 'inline' : 'none' }} >
                  <SVG src={this.state.backImage} onClick={this.onBodyClick.bind(this)} onLoad={this.syncSelection.bind(this)} />
                </div>
              </div>

              <Navigator
                onButtonNext={this.submit.bind(this)}
                enableNext={true}
                replaceWithMessage={selection.length === 0 ? 'Select a body part to start' : false}
                submitting={this.state.submitting}
                hidePrevious
              />
            </div>
          </div>
        }
      />
    );
  }

  submit() {
    this.setState({ submitting: true });

    SegmentAnalytics.track('Body locations entered', {
      locations: this.state.selection.map(s => s.id)
    });

    this.props.clinicalData.setBodyLocation(this.state.selection);
  }

  isQuerying() {
    return this.state.query !== '' || this.state.queryRegion !== '';
  }

  async onQueryChange(query) {
    await this.setState({ query });
    this.syncExpanded();
  }

  onBodyClick(event) {
    const element = event.target.closest('[data-name]');

    this.onQueryRegionChange(element ? element.getAttribute('data-name') : undefined);
  }

  async onFlipClick(/*event*/) {
    let { flipped } = this.state;

    flipped = !flipped;

    await this.setState({ flipped });
    this.syncSelection();
  }

  async onQueryRegionChange(queryRegion = '') {
    queryRegion = queryRegion.replace(/^left-|^right-/, '').replace(/-/, '_').toLowerCase();

    await this.setState({ queryRegion: queryRegion, query: '' });

    const matches = this.matches();
    const [firstMatch] = matches;

    if (matches.length <= 1) {
      await this.setState({ queryRegion: '', query: '' });

      if (firstMatch) {
        this.toggleSelection(firstMatch);
      }
    }

    this.syncExpanded();
  }

  syncExpanded() {
    const sidebarEl = this.qaRef && this.qaRef.sidebarEl;

    if (sidebarEl) {
      if (this.state.queryRegion) {
        sidebarEl.style.animation = 'bell 0.5s';
        sidebarEl.addEventListener('animationend', () => sidebarEl.style.animation = '');
        sidebarEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      sidebarEl.classList.toggle('expanded', this.isQuerying());
    }
  }

  syncSelection() {
    const { selection } = this.state;

    document
      .querySelectorAll('[data-name]:not([data-name=body])')
      .forEach(element => element.style.opacity = '0');

    selection.forEach(item => {
      const className = item.region.replace(/_/g, '-');

      document
        .querySelectorAll(`[data-name=${className}],[data-name=left-${className}],[data-name=right-${className}]`)
        .forEach(element => element.style.opacity = '1');
    });
  }

  isSelected(item) {
    return !!this.state.selection.find(element => element.id == item.id);
  }

  async toggleSelection(item) {
    let { selection } = this.state;
    const index = selection.findIndex(element => element.id == item.id);

    if (this.isSelected(item)) {
      selection.splice(index, 1);
    } else {
      selection.push(item);
    }

    await this.setState({ selection });
    this.syncSelection();
  }

  matches() {
    const { query, queryRegion, locations } = this.state;

    return locations
      .filter(condition => query.split(/ +/).every(token => RegExp(token, 'i').test(condition.label)))
      .filter(condition => queryRegion === '' || condition.region === queryRegion );
  }
}

BodyLocator.defaultProps = {};

BodyLocator.propTypes = {
  clinicalData: PropTypes.object.isRequired,
};

export { BodyLocator };
export default withClinicalData(BodyLocator);
